/**
 * Thread API — typed service functions for every endpoint threadsSlice talks
 * to, on the shared `apiClient` (which carries the 402→checkout interceptor).
 *
 * First slice migrated under roadmap Module 7 (API layer consolidation) — the
 * template for moving the remaining slices' raw axios calls behind
 * src/services/. Conventions:
 *  - Functions return `response.data`; axios errors propagate untouched so
 *    slice thunks keep their existing rejectWithValue shapes.
 *  - Auth uses `bearerAuthHeaders()` from apiClient (the single home for the
 *    previously copy-pasted Authorization header). Thunks that resolve their
 *    token through `withAuthRetry` pass it in explicitly.
 *
 * Roadmap: docs/domain-refactor-roadmap.md (Module 7).
 */
import apiClient from "src/services/apiClient";
import { bearerAuthHeaders } from "src/services/authHeaders";

export const fetchAllThreads = async <T = unknown>(token?: string | null): Promise<T> => {
  const response = await apiClient.get("/api/user/list-threads", { headers: bearerAuthHeaders(token) });
  return response.data as T;
};

export const fetchDocumentLibrary = async <T = unknown>(token?: string | null): Promise<T> => {
  const response = await apiClient.get("/api/user/document-library", { headers: bearerAuthHeaders(token) });
  return response.data as T;
};

export const deleteThreadById = async <T = unknown>(threadId: string): Promise<T> => {
  const response = await apiClient.delete(`/api/thread/${threadId}/delete`, { headers: bearerAuthHeaders() });
  return response.data as T;
};

/** Posts the caller's payload verbatim — the endpoint reads `title` off it. */
export const updateThreadTitle = async <T = unknown>(threadId: string, payload: unknown): Promise<T> => {
  const response = await apiClient.post(`/api/thread/${threadId}/update-title`, payload, {
    headers: bearerAuthHeaders(),
  });
  return response.data as T;
};

export const markThreadReadById = async <T = unknown>(threadId: string): Promise<T> => {
  const response = await apiClient.get(`/api/thread/${threadId}/mark-read`, { headers: bearerAuthHeaders() });
  return response.data as T;
};

export const fetchThreadTitle = async <T = unknown>(threadId: string): Promise<T> => {
  const response = await apiClient.get(`/api/v0/thread/${threadId}/get-title`, { headers: bearerAuthHeaders() });
  return response.data as T;
};

export const fetchDraftDownloadUrl = async <T = unknown>(threadId: string, type: string): Promise<T> => {
  const response = await apiClient.get(`/api/draft/${threadId}/${type}/download`, { headers: bearerAuthHeaders() });
  return response.data as T;
};

export const fetchReferenceDocDownloadUrl = async <T = unknown>(threadId: string, storageId: string): Promise<T> => {
  const response = await apiClient.post(
    `/api/thread/${threadId}/${storageId}/get-attachment-download-url`,
    {},
    { headers: bearerAuthHeaders() },
  );
  return response.data as T;
};

export const renameDraftDocument = async <T = unknown>(threadId: string, newName: string): Promise<T> => {
  const response = await apiClient.patch(
    `/api/draft/${threadId}/rename-document`,
    { new_name: newName },
    { headers: bearerAuthHeaders() },
  );
  return response.data as T;
};

export const fetchUnreadThreads = async <T = unknown>(): Promise<T> => {
  const response = await apiClient.get("/api/user/unread-threads", { headers: bearerAuthHeaders() });
  return response.data as T;
};

// ── Secure Sharing ────────────────────────────────────────────────────────────

export interface PublicShareLink {
  url: string;
  expires_at: string;
}

export interface SharedRecipient {
  email: string;
  share_url: string;
  granted_at: string;
  expires_at: string;
}

export interface ThreadAccessResponse {
  thread_id: string;
  shared_access_type: "public" | "named" | "private";
  public_share_link: PublicShareLink | null;
  shared_recipients: SharedRecipient[];
}

export interface RevokeAccessResponse {
  thread_id: string;
  shared_access_type: "public" | "named" | "private";
  revoked_recipient: { email: string };
  other_threads_affected: number;
}

export const getThreadAccess = async (threadId: string): Promise<ThreadAccessResponse> => {
  const response = await apiClient.get(`/api/thread/${threadId}/share-access`, { headers: bearerAuthHeaders() });
  return response.data;
};

export const updateThreadAccess = async (
  threadId: string,
  body: {
    shared_access_type: "public" | "named" | "private";
    email?: string;
  },
): Promise<ThreadAccessResponse> => {
  const response = await apiClient.put(`/api/thread/${threadId}/share-access`, body, { headers: bearerAuthHeaders() });
  return response.data;
};

export const revokeThreadAccess = async (
  threadId: string,
  body: { email: string; revoke_all_threads?: boolean },
): Promise<RevokeAccessResponse> => {
  const response = await apiClient.delete(`/api/thread/${threadId}/share-access`, {
    headers: bearerAuthHeaders(),
    data: body,
  });
  return response.data;
};

export const getShareLinkMessages = async <T = unknown>(threadId: string): Promise<T> => {
  // No auth header for public links; include if available for named links
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const accessToken = localStorage.getItem("accessToken2");
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }
  const response = await apiClient.get(`/api/thread/${threadId}/get-shared-thread-messages`, { headers });
  return response.data as T;
};
